// ЕДИНСТВЕННЫЙ источник представления карты (+ общие текстовые утилиты UI).
// Карта в любом виде — мини-карта на столе, крупная карточка подготовки, плашка в журнале — собирается
// preact-компонентом Card/CardChipV отсюда; экранам запрещено верстать карту самим (инвариант закреплён
// тестом test/ui-surfaces.ts). Сквозные элементы (сноска ребаланса, иконки, ★) добавляются здесь один раз
// и появляются на всех экранах сразу.
import { h, Fragment } from 'preact';
import type { JSX } from 'preact';
import { render as toString } from 'preact-render-to-string';
import type { CardDef, CardId, CardRef, CardType, TraitId } from '../types.ts';
import { card, cardOrNull, traitDef } from '../engine/state.ts';
import { REBALANCE, REBALANCE_TRAIT_NOTES } from '../engine/rebalance.ts';
import type { UiCommand } from './commands.ts';
import { cmdProps } from './commands.ts';

/** Значок чипсины: оранжевый треугольник (эмодзи-треугольник бывает только красным) */
export const CHIP_IC = '<span class="cticon">▲</span>';
export const ChipIc = (): JSX.Element => h('span', { class: 'cticon' }, '▲');
const chipIc = ChipIc;

const ESC: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
export const esc = (s: unknown): string => String(s ?? '').replace(/[&<>"']/g, (m) => ESC[m] ?? m);

export function fmtText(t: string | null | undefined): string {
  return esc(t)
    .replace(/(Атака:|Неизбежная атака:|Защита:|Постоянка:?|☀ Атака:|☀:|В конце игры:|В КОНЦЕ ИГРЫ:|Уничтожь эту карту:)/g, '<b>$1</b>')
    .replace(/\n/g, '<br>');
}
/** Текст карты/жетона компонентом (жирные ключевые слова, переносы) */
export const Txt = ({ cls, text }: { cls: string; text: string | null | undefined }): JSX.Element =>
  h('div', { class: cls, dangerouslySetInnerHTML: { __html: fmtText(text) } });
const textV = (cls: string, t: string): JSX.Element => h(Txt, { cls, text: t });

/** У вялых палочек и шальной магии типа нет (правила: ими нельзя сбросить «карту такого-то типа») */
export const NO_TYPE: ReadonlySet<CardType> = new Set<CardType>(['вялая палочка', 'шальная магия']);
export function typeLabel(d: CardDef): string {
  if (NO_TYPE.has(d.type)) return '';
  return (d.legend ? 'Легенда — ' : '') + d.type;
}
export const TYPE_CLASS: Record<CardType, string> = {
  'тварь': 't-beast', 'волшебник': 't-wizard', 'заклинание': 't-spell', 'сокровище': 't-treasure',
  'место': 't-place', 'фамильяр': 't-familiar', 'затравка': 't-starter', 'шальная магия': 't-chaos',
  'вялая палочка': 't-limp', 'беспредел': 't-bespredel', 'мегабеспредел': 't-bespredel',
};

function iconsV(d: CardDef): JSX.Element[] {
  const icons: JSX.Element[] = [];
  if (d.attack) icons.push(h('span', { class: 'ic ic-a', title: 'атака' }, '⚔'));
  if (d.defense) icons.push(h('span', { class: 'ic ic-d', title: 'защита' }, '🛡'));
  if (d.permanent) icons.push(h('span', { class: 'ic ic-p', title: 'постоянка' }, '∞'));
  if (d.activation) icons.push(h('span', { class: 'ic ic-s', title: 'активация' }, '☀'));
  if (d.chipsina) icons.push(h('span', { class: 'ic ic-c', title: 'чипсина' }, chipIc()));
  if (isRebalanced(d.id)) icons.push(h('span', { class: 'ic ic-reb', title: 'Карта правлена для режима ребаланса: её цена и текст здесь — уже правленые' }, '⚖'));
  return icons;
}
export function cardIcons(d: CardDef): string { return toString(h(Fragment, null, iconsV(d))); }

/** Зона карты в разметке (data-zone) — по ней обработчики понимают, что значит тап/перетаскивание */
export type CardZone = 'hand' | 'table' | 'market' | 'pick' | 'defend' | 'ctx' | 'token' | 'freefam' | 'view';

/**
 * Что рисуем: экземпляр карты (data-iid), стопку (data-pile — например шальная магия) или просто описание
 * (data-cid; выбор ничейного фамильяра в подготовке)
 */
export type CardSubject = { inst: CardRef } | { pile: 'chaos'; cid: CardId } | { cid: CardId };

export interface CardHtmlOpts {
  /** 'mini' (по умолчанию) — карта на столе/барахолке/в руке; 'setup' — крупная карточка экрана подготовки */
  variant?: 'mini' | 'setup';
  cls?: string;
  /** чипсины на карте барахолки */
  chips?: number;
  price?: number | null;
  oldPrice?: number | null;
  /** можно применить ☀ */
  act?: boolean;
  selected?: boolean;
  dim?: boolean;
  /** true/false — по карману или нет; null — не показывать */
  afford?: boolean | null;
  /** размер стопки */
  count?: number | null;
  /** ключ для анимаций (по умолчанию c:iid) */
  key?: string | null;
  zone?: CardZone;
  /** метка сверху */
  tag?: string;
  used?: boolean;
  /** html кнопки внутри карты (строковые экраны) */
  btn?: string;
  /** кнопка внутри карты компонентом */
  btnV?: JSX.Element | null;
  /** команда по тапу (карточки подготовки) */
  tapCmd?: UiCommand;
}

/** Бонус ПО карты для смотрящего игрока (жетон «+1 ПО за сокровище», Гусыня, Гикпиг, палочки…) — задаёт приложение,
 *  когда известно, кто смотрит; null — не показывать */
export type VpBonusFn = (c: CardRef) => { value: number; labels: string[] } | null;
let vpBonus: VpBonusFn | null = null;
export function setVpContext(fn: VpBonusFn | null): void { vpBonus = fn; }

/** Партия в режиме ребаланса? — задаёт приложение; в режиме правленые карты несут значок ⚖ (сама карта уже правленая) */
let rebalanceOn: () => boolean = () => false;
export function setRebalanceContext(fn: () => boolean): void { rebalanceOn = fn; }
/**
 * Правлена ли карта в идущей партии. Содержание правки на карте не пишем: цена и текст на ней — уже правленые,
 * сноска «было → стало» рядом с ними только путала бы (заголовок и сноска говорили бы разное).
 * Сравнение с оригиналом — в справке режима и по кнопке «Оригинал» в крупном виде.
 */
export function isRebalanced(cid: CardId): boolean {
  return rebalanceOn() && !!REBALANCE[cid];
}

/** Правлен ли жетон свойства в идущей партии (его текст в базе — уже правленый) */
export function isTraitRebalanced(traitId: TraitId): boolean {
  return rebalanceOn() && !!REBALANCE_TRAIT_NOTES[traitDef(traitId).key];
}

/** ★ с бонусом: «★2 +1» (подсказка — за что); без напечатанных ★ — «★+1» / «★−1» */
function vpV(vp: number | null | undefined, bonus: { value: number; labels: string[] } | null): JSX.Element | null {
  const b = bonus?.value ?? 0;
  if (!vp && !b) return null;
  const plus = b ? h('i', { class: `vp-plus ${b < 0 ? 'neg' : ''}` }, `${b > 0 ? '+' : '−'}${Math.abs(b)}`) : null;
  return h('span', { class: `vp ${b ? 'has-plus' : ''}`, ...(bonus && b ? { title: bonus.labels.join('; ') } : {}) }, `★${vp || ''}`, plus);
}

function subjectProps(s: CardSubject): { cid: CardId; props: Record<string, string | number>; key: string } {
  if ('inst' in s) return { cid: s.inst.cid, props: { 'data-iid': s.inst.iid }, key: `c:${s.inst.iid}` };
  if ('pile' in s) return { cid: s.cid, props: { 'data-pile': s.pile }, key: `c:${s.pile}` };
  return { cid: s.cid, props: {}, key: `cid:${s.cid}` };
}

const artImg = (d: CardDef): JSX.Element =>
  h('img', { src: d.art ?? d.image, alt: '', loading: 'lazy', draggable: false });

export interface CardProps extends CardHtmlOpts { subject: CardSubject }

/** Карта — единственный компонент её представления; используется всеми экранами через cardHtml/прямо */
export function Card(p: CardProps): JSX.Element {
  const { subject, variant = 'mini', cls = '', chips = 0, price = null, oldPrice = null, act = false, selected = false, dim = false, afford = null, count = null, key = null, zone, tag = '', used = false, btn = '', btnV = null, tapCmd } = p;
  const s = subjectProps(subject);
  const d = card(s.cid);
  const tap = tapCmd ? cmdProps(tapCmd) : {};
  if (variant === 'setup') {
    // крупная карточка выбора (подготовка): арт, имя, полный текст, сноска правки, чей фамильяр
    return h('div', { class: ['setup-card', selected ? 'sel' : '', cls].filter(Boolean).join(' '), 'data-cid': d.id, ...tap },
      artImg(d),
      h('b', null, d.name),
      textV('cardtext', d.text),
      d.familiarOf.length ? h('div', { class: 'sub' }, `Колдун: ${d.familiarOf.join(' / ')}`) : null);
  }
  const text = d.text && d.text !== '(Эффекта нет.)' ? d.text : '';
  const discounted = oldPrice != null && oldPrice !== price;
  const classes = ['card', TYPE_CLASS[d.type] ?? '', d.legend ? 'legend' : '', cls, act ? 'act' : '', used ? 'used' : '',
    selected ? 'selected' : '', dim ? 'dim' : '', afford === true ? 'buyable' : afford === false ? 'noafford' : ''];
  return h('div', { class: classes.filter(Boolean).join(' '), ...s.props, 'data-cid': d.id, 'data-fx': key ?? s.key, ...(zone ? { 'data-zone': zone } : {}), ...tap },
    h('div', { class: 'art' },
      artImg(d),
      price != null ? h('div', { class: `cost ${discounted ? 'disc' : ''}` }, price) : null,
      discounted ? h('div', { class: 'cost-old', title: 'со скидкой' }, oldPrice) : null,
      chips ? h('div', { class: 'chips' }, chipIc(), chips) : null,
      count != null ? h('div', { class: 'cnt' }, `×${count}`) : null,
      act ? h('div', { class: 'actdot' }, '☀') : null,
      tag ? h('div', { class: 'tag' }, tag) : null),
    h('div', { class: 'nm' }, d.name),
    h('div', { class: 'meta' },
      d.legend ? h('span', { class: 'lg' }, 'легенда') : null,
      h('span', { class: 'ty' }, NO_TYPE.has(d.type) ? '' : d.type),
      h('span', { class: 'icons' }, iconsV(d)),
      vpV(d.vp, vpBonus ? vpBonus('inst' in subject ? subject.inst : { iid: -1, cid: s.cid }) : null)),
    text ? textV(`txt${text.length > 130 ? ' long' : ''}`, text) : null,
    btn ? h('div', { class: 'cardbtn', dangerouslySetInnerHTML: { __html: btn } }) : btnV ? h('div', { class: 'cardbtn' }, btnV) : null);
}

/** Компактная плашка карты (ленты событий, журнал, справка); null, если карты нет в базе */
export function CardChipV({ cid }: { cid: CardId | null | undefined }): JSX.Element | null {
  const d = cardOrNull(cid);
  if (!d) return null;
  // тап по плашке — полное описание карты (в журнале, ленте, вопросе о защите, справке ребаланса)
  return h('span', { class: `cchip ${TYPE_CLASS[d.type] ?? ''}`, role: 'button', title: 'подробнее', ...cmdProps({ k: 'zoomCid', cid: d.id }) },
    h('img', { src: d.art ?? d.image, alt: '', draggable: false }),
    h('span', null, d.name));
}

/** Карта vnode'ом с ключом для diff'а (ключ — тот же, что data-fx: перестановки анимируются, а не пересоздаются) */
export const cardV = (subject: CardSubject, opts: CardHtmlOpts = {}): JSX.Element =>
  h(Card, { key: opts.key ?? subjectProps(subject).key, subject, ...opts });

// ---------- строковые обёртки: экраны на HTML-строках зовут их с прежними сигнатурами ----------
export function cardHtml(subject: CardSubject, opts: CardHtmlOpts = {}): string {
  return toString(h(Card, { subject, ...opts }));
}
export function cardChip(cid: CardId | null | undefined): string {
  return toString(h(CardChipV, { cid }));
}
